import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { useCompetition, useSetExhibitionSelection, useCompetitionIdParam } from '@/hooks/useCompetitions';
import { useCategories } from '@/hooks/useCategories';
import { useRanking } from '@/hooks/useJudging';
import {
  useAwards,
  useAwardTypes,
  useCreateAward,
  useDeleteAward,
  useDeleteAwardType,
  useSaveAwardType,
} from '@/hooks/useAwards';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState, Alert } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { CompetitionWorkspaceHeader } from '@/components/organizer/CompetitionWorkspace';

import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Table, type Column } from '@/components/ui/Table';
import { SubmissionThumbnail } from '@/components/submissions/SubmissionThumbnail';
import {
  CompetitionStatus,
  ExhibitionSelectionMode,
  ExhibitionSelectionScope,
  type Award,
  type AwardType,
  type SubmissionRanking,
} from '@/api/types';

interface TypeForm {
  name: string;
  description: string;
  categoryId: string;
  rank: string;
  quantity: string;
}

const emptyTypeForm: TypeForm = { name: '', description: '', categoryId: '', rank: '1', quantity: '1' };

/** Typical share of entries a salon accepts for its exhibition: 20-35 %. */
const DEFAULT_EXHIBITION_PERCENT = 25;
const DEFAULT_EXHIBITION_COUNT = 5;

const formatScore = (score: number) => score.toFixed(2).replace('.', ',');

function rankingCountSelected(items: SubmissionRanking[]) {
  return items.filter((s) => s.isSelected).length;
}

export function OrganizerAwardsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { competitionId } = useCompetitionIdParam();
  const competition = useCompetition(competitionId);
  const categories = useCategories(competitionId);
  const awardTypes = useAwardTypes(competitionId);
  const awards = useAwards(competitionId);
  const ranking = useRanking(competitionId);
  const saveType = useSaveAwardType(competitionId);
  const deleteType = useDeleteAwardType(competitionId);
  const createAward = useCreateAward(competitionId);
  const deleteAward = useDeleteAward(competitionId);
  const setSelection = useSetExhibitionSelection(competitionId);

  const [form, setForm] = useState<TypeForm>(emptyTypeForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [giving, setGiving] = useState<SubmissionRanking | null>(null);
  const [removingAward, setRemovingAward] = useState<Award | null>(null);
  const [removingType, setRemovingType] = useState<AwardType | null>(null);
  const [valueInput, setValueInput] = useState<string | null>(null);
  const [modeInput, setModeInput] = useState<ExhibitionSelectionMode | null>(null);
  const [scopeInput, setScopeInput] = useState<ExhibitionSelectionScope | null>(null);

  const types = [...(awardTypes.data?.items ?? [])].sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
  const given = awards.data?.items ?? [];
  const categoryName = (id?: string | null) => (id ? categories.data?.items.find((c) => c.id === id)?.name ?? '' : '');
  const status = competition.data?.status ?? 0;
  const finished = status >= CompetitionStatus.Awarding;
  const isAwarding = status === CompetitionStatus.Awarding;
  const saved = competition.data?.exhibitionSelection ?? null;
  const mode = modeInput ?? saved?.mode ?? ExhibitionSelectionMode.Percent;
  const scope = scopeInput ?? saved?.scope ?? ExhibitionSelectionScope.PerCategory;
  const valueText = valueInput ?? String(saved?.value ?? (mode === ExhibitionSelectionMode.Percent ? DEFAULT_EXHIBITION_PERCENT : DEFAULT_EXHIBITION_COUNT));
  const valueNumber = parseInt(valueText, 10);
  const valueValid =
    Number.isInteger(valueNumber) && valueNumber >= 1 && (mode === ExhibitionSelectionMode.Count || valueNumber <= 100);
  const describeSelection = (s: { mode: ExhibitionSelectionMode; scope: ExhibitionSelectionScope; value: number }) =>
    `${s.mode === ExhibitionSelectionMode.Percent ? `${s.value} % najboljših` : `${s.value} najboljših`} ${
      s.scope === ExhibitionSelectionScope.PerCategory ? 'v vsaki kategoriji' : 'skupaj, ne glede na kategorijo'
    }`;
  const selectedCount = rankingCountSelected(ranking.data?.items ?? []);

  const givenCount = (typeId: string) => given.filter((a) => a.awardTypeId === typeId).length;
  const rankingItems = [...(ranking.data?.items ?? [])].sort((a, b) => a.rank - b.rank);
  const visibleRanking = categoryFilter
    ? rankingItems.filter((s) => s.categoryId === categoryFilter)
    : rankingItems;
  // Entries with the same average are tied: the jury decides who gets the award, the organizer then gives it.
  // The place is counted among the entries on screen, so a category filter gives the place within that category.
  const tieSize = (s: SubmissionRanking) => visibleRanking.filter((o) => o.averageScore === s.averageScore).length;
  const placeOf = (s: SubmissionRanking) => 1 + visibleRanking.filter((o) => o.averageScore > s.averageScore).length;
  const hasTies = visibleRanking.some((s) => tieSize(s) > 1);
  const titleOf = (submissionId: string) => rankingItems.find((s) => s.submissionId === submissionId)?.title ?? '';

  /** Awards that can still be given to this entry: its category, not full, not already received. */
  function eligibleTypes(entry: SubmissionRanking) {
    return types.filter(
      (t) =>
        (!t.categoryId || t.categoryId === entry.categoryId) &&
        givenCount(t.id) < t.quantity &&
        !given.some((a) => a.awardTypeId === t.id && a.submissionId === entry.submissionId),
    );
  }

  function startEditing(t: AwardType) {
    saveType.reset();
    setEditingId(t.id);
    setForm({
      name: t.name,
      description: t.description ?? '',
      categoryId: t.categoryId ?? '',
      rank: String(t.rank),
      quantity: String(t.quantity),
    });
  }

  function resetForm() {
    saveType.reset();
    setEditingId(null);
    setForm(emptyTypeForm);
  }

  async function handleSaveType(e: FormEvent) {
    e.preventDefault();
    await saveType
      .mutateAsync({
        id: editingId ?? undefined,
        payload: {
          name: form.name.trim(),
          description: form.description.trim() || null,
          categoryId: form.categoryId || null,
          rank: Math.max(1, parseInt(form.rank, 10) || 1),
          quantity: Math.max(1, parseInt(form.quantity, 10) || 1),
        },
      })
      .then(resetForm)
      .catch(() => undefined);
  }

  async function handleGive(entry: SubmissionRanking, awardTypeId: string) {
    await createAward
      .mutateAsync({ submissionId: entry.submissionId, awardTypeId })
      .then(() => setGiving(null))
      .catch(() => undefined);
  }

  const columns: Column<SubmissionRanking>[] = [
    {
      header: '#',
      accessor: (s) => (
        <span className="inline-flex items-center gap-2">
          <span className="font-mono tabular-nums">{placeOf(s)}</span>
          {tieSize(s) > 1 && <Badge tone="Judging">Izenačeno</Badge>}
        </span>
      ),
    },
    {
      header: '',
      accessor: (s) => (
        <Link to={`/submissions/${s.submissionId}`} className="block h-14 w-14 overflow-hidden rounded-photo bg-surface-3">
          <SubmissionThumbnail submissionId={s.submissionId} alt={s.title} />
        </Link>
      ),
    },
    {
      header: 'Prispevek',
      accessor: (s) => (
        <Link to={`/submissions/${s.submissionId}`} className="font-medium text-fg hover:underline">
          {s.title}
        </Link>
      ),
    },
    ...(categoryFilter ? [] : [{ header: 'Kategorija', accessor: (s: SubmissionRanking) => categoryName(s.categoryId) }]),
    {
      header: 'Ocena',
      className: 'text-right font-mono tabular-nums',
      accessor: (s) => formatScore(s.averageScore),
    },
    {
      header: 'Št. ocen',
      className: 'text-right font-mono tabular-nums',
      accessor: (s) => s.scoreCount,
    },
    {
      header: 'Razstava',
      accessor: (s) => (s.isSelected ? <Badge tone="Scored">Izbrano</Badge> : <span className="text-fg-muted">—</span>),
    },
    {
      header: 'Nagrade',
      accessor: (s) => {
        const mine = given.filter((a) => a.submissionId === s.submissionId);
        return mine.length === 0 ? (
          <span className="text-fg-muted">—</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {mine.map((a) => (
              <Badge key={a.id} tone="Accepted">
                {a.title}
              </Badge>
            ))}
          </span>
        );
      },
    },
    {
      header: '',
      accessor: (s) => (
        <Button
          size="sm"
          variant="secondary"
          disabled={eligibleTypes(s).length === 0}
          onClick={() => {
            createAward.reset();
            setGiving(s);
          }}
        >
          Podeli nagrado
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div>
        {!embedded && <CompetitionWorkspaceHeader competitionId={competitionId} title="Nagrade" />}
        <p className="text-sm text-fg-muted">
          {embedded
            ? 'Določite, katere nagrade obstajajo (na primer Grand Prix, 1. mesto). Podelite jih po ocenjevanju na strani »Nagrade«.'
            : 'Najprej določite, katere nagrade obstajajo. Ko je ocenjevanje zaključeno (stanje »Podeljevanje nagrad«), določite, kolikšen delež najboljših gre na razstavo, in podelite nagrade po razvrstitvi.'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-fg">{embedded ? 'Nagrade tekmovanja' : '1. Nagrade tekmovanja'}</h2>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          {awardTypes.isLoading && <PageSpinner />}
          {awardTypes.isError && (
            <ErrorState message={getApiErrorMessage(awardTypes.error)} onRetry={() => awardTypes.refetch()} />
          )}
          {awardTypes.isSuccess && types.length === 0 && (
            <EmptyState title="Ni še določenih nagrad" description="Spodaj dodajte prvo nagrado, na primer »Grand Prix« ali »1. mesto«." />
          )}
          {types.length > 0 && (
            <ul className="flex flex-col divide-y divide-line rounded-card border border-line">
              {types.map((t) => (
                <li key={t.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div>
                    <p className="font-medium text-fg">
                      <span className="mr-2 font-mono text-sm tabular-nums text-fg-muted">#{t.rank}</span>
                      {t.name}
                    </p>
                    <p className="text-sm text-fg-muted">
                      {t.categoryId ? `Kategorija: ${categoryName(t.categoryId)}` : 'Vse kategorije'}
                      {' · '}podeljeno {givenCount(t.id)} od {t.quantity}
                    </p>
                    {t.description && <p className="text-sm text-fg-secondary">{t.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => startEditing(t)}>
                      Uredi
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        deleteType.reset();
                        setRemovingType(t);
                      }}
                    >
                      Izbriši
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <form onSubmit={handleSaveType} className="flex flex-col gap-3 border-t border-line pt-4">
            <h3 className="text-sm font-semibold text-fg">{editingId ? 'Urejanje nagrade' : 'Nova nagrada'}</h3>
            {saveType.isError && <Alert variant="error">{getApiErrorMessage(saveType.error)}</Alert>}
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Naziv nagrade"
                value={form.name}
                maxLength={200}
                required
                placeholder="Grand Prix"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              <Select
                label="Kategorija (neobvezno)"
                value={form.categoryId}
                onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
              >
                <option value="">Vse kategorije</option>
                {categories.data?.items.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Select>
              <Input
                label="Mesto"
                type="number"
                min={1}
                value={form.rank}
                onChange={(e) => setForm((f) => ({ ...f, rank: e.target.value }))}
              />
              <Input
                label="Število prejemnikov"
                type="number"
                min={1}
                max={100}
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              />
            </div>
            <Input
              label="Opis (neobvezno)"
              value={form.description}
              maxLength={1000}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
            <div className="flex gap-2">
              <Button type="submit" isLoading={saveType.isPending} disabled={!form.name.trim()}>
                {editingId ? 'Shrani' : 'Dodaj nagrado'}
              </Button>
              {editingId && (
                <Button type="button" variant="secondary" onClick={resetForm}>
                  Prekliči
                </Button>
              )}
            </div>
          </form>
        </CardBody>
      </Card>

      {!embedded && (
        <>
      <Card>
        <CardHeader>
          <h2 className="font-semibold text-fg">2. Izbor za razstavo</h2>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          {!finished ? (
            <Alert variant="info">
              Izbor za razstavo je mogoč, ko je ocenjevanje zaključeno in je tekmovanje v stanju »Podeljevanje nagrad«.
            </Alert>
          ) : (
            <>
              <p className="text-sm text-fg-secondary">
                Vsi sprejeti prispevki so že v tekmovanju. Tu določite, koliko najboljših (po povprečni oceni) je izbranih za
                razstavo: kot delež ali kot število, v vsaki kategoriji posebej ali skupaj. Prispevki, ki so na meji
                izenačeni, so izbrani skupaj.
              </p>
              {setSelection.isError && <Alert variant="error">{getApiErrorMessage(setSelection.error)}</Alert>}
              {isAwarding ? (
                <div className="flex flex-col gap-3">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <fieldset className="flex flex-col gap-1.5">
                      <legend className="text-sm font-medium text-fg-secondary">Koliko</legend>
                      <label className="flex items-center gap-2 text-sm text-fg">
                        <input
                          type="radio"
                          name="exhibition-mode"
                          checked={mode === ExhibitionSelectionMode.Percent}
                          onChange={() => {
                            setModeInput(ExhibitionSelectionMode.Percent);
                            setValueInput(null);
                          }}
                        />
                        Delež najboljših (%)
                      </label>
                      <label className="flex items-center gap-2 text-sm text-fg">
                        <input
                          type="radio"
                          name="exhibition-mode"
                          checked={mode === ExhibitionSelectionMode.Count}
                          onChange={() => {
                            setModeInput(ExhibitionSelectionMode.Count);
                            setValueInput(null);
                          }}
                        />
                        Število najboljših
                      </label>
                    </fieldset>
                    <fieldset className="flex flex-col gap-1.5">
                      <legend className="text-sm font-medium text-fg-secondary">Kje</legend>
                      <label className="flex items-center gap-2 text-sm text-fg">
                        <input
                          type="radio"
                          name="exhibition-scope"
                          checked={scope === ExhibitionSelectionScope.PerCategory}
                          onChange={() => setScopeInput(ExhibitionSelectionScope.PerCategory)}
                        />
                        V vsaki kategoriji posebej
                      </label>
                      <label className="flex items-center gap-2 text-sm text-fg">
                        <input
                          type="radio"
                          name="exhibition-scope"
                          checked={scope === ExhibitionSelectionScope.Overall}
                          onChange={() => setScopeInput(ExhibitionSelectionScope.Overall)}
                        />
                        Skupaj, razvrščeno po oceni
                      </label>
                    </fieldset>
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="w-40">
                      <Input
                        label={mode === ExhibitionSelectionMode.Percent ? 'Delež najboljših (%)' : 'Število najboljših'}
                        type="number"
                        min={1}
                        max={mode === ExhibitionSelectionMode.Percent ? 100 : undefined}
                        value={valueText}
                        onChange={(e) => setValueInput(e.target.value)}
                      />
                    </div>
                    <Button
                      isLoading={setSelection.isPending}
                      disabled={!valueValid}
                      onClick={() => setSelection.mutate({ mode, scope, value: valueNumber })}
                    >
                      {saved ? 'Izberi znova' : 'Izberi najboljše'}
                    </Button>
                  </div>
                </div>
              ) : (
                <Alert variant="info">
                  Rezultati so objavljeni. Izbor lahko spremenite, če tekmovanje vrnete na podeljevanje nagrad.
                </Alert>
              )}
              {saved ? (
                <p className="text-sm text-fg">
                  Za razstavo je izbranih <strong>{selectedCount}</strong> od {rankingItems.length} ocenjenih prispevkov (
                  {describeSelection(saved)}).
                </p>
              ) : (
                <p className="text-sm text-fg-muted">Izbor še ni narejen.</p>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-fg">3. Podelitev nagrad</h2>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          {!finished ? (
            <Alert variant="info">
              Nagrade je mogoče podeliti, ko je ocenjevanje zaključeno (stanje »Podeljevanje nagrad«). Takrat se tu
              prikaže razvrstitev prispevkov po povprečni oceni.
            </Alert>
          ) : (
            <>
              {types.length === 0 && <Alert variant="info">Za podelitev najprej določite vsaj eno nagrado zgoraj.</Alert>}
              {hasTies && (
                <Alert variant="info">
                  Nekateri prispevki so izenačeni (isto mesto). O zmagovalcu odloči žirija; nagrado nato podelite
                  izbranemu prispevku.
                </Alert>
              )}
              {createAward.isError && !giving && <Alert variant="error">{getApiErrorMessage(createAward.error)}</Alert>}
              <div className="max-w-sm">
                <Select
                  label="Kategorija (neobvezno)"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">Vse kategorije</option>
                  {categories.data?.items.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </Select>
              </div>
              {ranking.isLoading && <PageSpinner />}
              {ranking.isError && <ErrorState message={getApiErrorMessage(ranking.error)} onRetry={() => ranking.refetch()} />}
              {ranking.isSuccess && visibleRanking.length === 0 && <EmptyState title="Ni ocenjenih prispevkov" />}
              {ranking.isSuccess && visibleRanking.length > 0 && (
                <Table columns={columns} rows={visibleRanking} getRowKey={(s) => s.submissionId} />
              )}
            </>
          )}
        </CardBody>
      </Card>

      {given.length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold text-fg">Podeljene nagrade</h2>
          </CardHeader>
          <CardBody className="flex flex-col divide-y divide-line">
            {[...given]
              .sort((a, b) => a.rank - b.rank)
              .map((award) => (
                <div key={award.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3">
                    <Trophy size={18} className="text-accent-text" aria-hidden="true" />
                    <div>
                      <p className="font-medium text-fg">
                        {award.title}
                        {award.categoryId && <span className="text-fg-muted"> &middot; {categoryName(award.categoryId)}</span>}
                      </p>
                      <Link to={`/submissions/${award.submissionId}`} className="text-sm text-fg-secondary hover:underline">
                        {titleOf(award.submissionId) || 'Prispevek'}
                      </Link>
                    </div>
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      deleteAward.reset();
                      setRemovingAward(award);
                    }}
                  >
                    Odvzemi
                  </Button>
                </div>
              ))}
          </CardBody>
        </Card>
      )}

      {giving && (
        <Modal title="Podeli nagrado" onClose={() => setGiving(null)}>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-fg-secondary">
              Prispevek »{giving.title}« · ocena {formatScore(giving.averageScore)} · mesto {placeOf(giving)}
              {tieSize(giving) > 1 && ' (izenačeno)'}
            </p>
            {createAward.isError && <Alert variant="error">{getApiErrorMessage(createAward.error)}</Alert>}
            <ul className="flex flex-col gap-2">
              {eligibleTypes(giving).map((t) => (
                <li key={t.id}>
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full justify-start"
                    isLoading={createAward.isPending && createAward.variables?.awardTypeId === t.id}
                    onClick={() => handleGive(giving, t.id)}
                  >
                    <Trophy size={14} aria-hidden="true" />
                    {t.name}
                    <span className="text-fg-muted">
                      ({givenCount(t.id)}/{t.quantity})
                    </span>
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </Modal>
      )}

      {removingAward && (
        <Modal title="Odvzemi nagrado" onClose={() => setRemovingAward(null)}>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-fg-secondary">
              Nagrada »{removingAward.title}« bo odvzeta prispevku »{titleOf(removingAward.submissionId) || 'Prispevek'}«.
            </p>
            {deleteAward.isError && <Alert variant="error">{getApiErrorMessage(deleteAward.error)}</Alert>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setRemovingAward(null)}>
                Prekliči
              </Button>
              <Button
                variant="danger"
                isLoading={deleteAward.isPending}
                onClick={() => deleteAward.mutate(removingAward.id, { onSuccess: () => setRemovingAward(null) })}
              >
                Odvzemi
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {removingType && (
        <Modal title="Izbriši nagrado" onClose={() => setRemovingType(null)}>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-fg-secondary">
              Nagrada »{removingType.name}« bo izbrisana. Že podeljene nagrade ostanejo prispevkom.
            </p>
            {deleteType.isError && <Alert variant="error">{getApiErrorMessage(deleteType.error)}</Alert>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setRemovingType(null)}>
                Prekliči
              </Button>
              <Button
                variant="danger"
                isLoading={deleteType.isPending}
                onClick={() => deleteType.mutate(removingType.id, { onSuccess: () => setRemovingType(null) })}
              >
                Izbriši
              </Button>
            </div>
          </div>
        </Modal>
      )}
        </>
      )}
    </div>
  );
}
